'use client'

import { useEffect } from 'react'

/**
 * WebMCP — exposes read-only portfolio tools to in-browser AI agents via the
 * experimental `navigator.modelContext` API (webmachinelearning/webmcp).
 * Uses registerTool() per the spec, with provideContext() as a fallback.
 * Fully feature-detected: a no-op in browsers without the API, so it can never
 * break the page or affect users.
 */
type ToolResult = { content: Array<{ type: string; text: string }> }
type WebMcpTool = {
  name: string
  description: string
  inputSchema: object
  execute: () => Promise<ToolResult>
}
type ModelContext = {
  registerTool?: (tool: WebMcpTool) => unknown
  provideContext?: (ctx: { tools: WebMcpTool[] }) => void
}

export default function WebMCP() {
  useEffect(() => {
    const mc = (navigator as Navigator & { modelContext?: ModelContext }).modelContext
    if (!mc) return

    const fetchText = async (url: string): Promise<string> => {
      try {
        const r = await fetch(url, { headers: { accept: 'application/json' } })
        return await r.text()
      } catch (e) {
        return `Error fetching ${url}: ${String(e)}`
      }
    }

    const tool = (name: string, description: string, url: string): WebMcpTool => ({
      name,
      description,
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      execute: async () => ({ content: [{ type: 'text', text: await fetchText(url) }] }),
    })

    const tools = [
      tool('get_abdorange_profile', 'Get abdorange profile: title, location, skills and contact links.', '/api/profile'),
      tool('list_abdorange_projects', 'List abdorange selected projects and products.', '/api/projects'),
      tool('list_abdorange_awards', 'List abdorange awards, recognition and media features.', '/api/awards'),
    ]

    try {
      if (typeof mc.registerTool === 'function') {
        tools.forEach((t) => mc.registerTool!(t))
      } else if (typeof mc.provideContext === 'function') {
        mc.provideContext({ tools })
      }
    } catch {
      /* ignore — experimental API shape may vary */
    }
  }, [])

  return null
}
